import axios, { AxiosRequestConfig, Method } from 'axios'
import { useAuthStore } from '../store/authStore'
import { sessionActions, captureRequestOrigin, RequestOrigin } from '../store/sessionActions'
import { showMessage } from '../utils/snackbar'
import { tokenRefresher } from '../services/firebase/tokenRefresher'
import { breadcrumb } from '../utils/sentry'
import i18n from '../i18n'

// Each request will use these defaults for retry logic unless overridden.
export const DEFAULT_RETRIES = 1
export const DEFAULT_RETRIES_DELAY = 1000

/**
 * Ceiling for every request that doesn't ask for more room. Without it, a promise that never gets a
 * response — a suspended tab, a sleeping laptop, a network transition — hangs forever, which used to
 * wedge single-flight guards like the chat's `isProcessing` for the life of the page, since their
 * `finally` never got a turn to run (MD-1503). 45s is well past what any of these ordinary requests
 * (chat turns, encounter CRUD, tenant init) take to answer.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 45_000

/**
 * Ceiling for report generation and modification, which the backend gives its own 600s deadline
 * (`FASTAPI_REQUEST_TIMEOUT`, answered with a 503). This sits just above it deliberately: while the
 * connection is alive the server's answer always wins, so we never abort work it would have
 * finished. It only bites when nothing is coming back at all — a killed pod, a dropped connection,
 * a sleeping laptop — where axios would otherwise wait forever and leave the caller hanging.
 */
export const LONG_REQUEST_TIMEOUT_MS = 630_000

// These errors are considered warning-only and will not trigger a full error log.
const warningOnlyErrors = new Set(['ERR_NETWORK'])
const warningOnlyStatuses = new Set([410])

// The detail the backend answers with on every request once an account has been deactivated (see the
// platform API's `services/authentication/user.py`). The response carries no machine-readable code,
// so this exact string is the only signal we get and the two sides are coupled by it.
const ACCOUNT_DISABLED_DETAIL = 'User account is disabled'

// The only 400 the accept-agreement/privacy-policy/usage-instructions endpoints can produce (see
// the backend's shared `_accept` helper) — another tab already accepted this exact flag.
const ALREADY_ACCEPTED_DETAIL = 'already_accepted'

// A 401 is deliberately absent: it is handled by renewing the credential once (see `request`),
// not by spending the generic retry budget on a token the server has already rejected.
const RETRY_STATUSES = new Set([
    408, // Request Timeout: the client was too slow sending the body (e.g. audio upload on a poor connection)
    429, // Too Many Requests
    502, // Bad Gateway
    503, // Service Unavailable
    504, // Gateway Timeout
])
const RETRY_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ENOTFOUND'])

let isApiInitialized = false
let initializationPromise: Promise<void> | null = null

// Create an Axios instance with authentication support
const api = axios.create({
    baseURL: import.meta.env.VITE_BACKEND_URL ?? '/{tenant}/api',
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
})

// Add request interceptor for authentication and FormData handling
api.interceptors.request.use(
    (config) => {
        const idToken = useAuthStore.getState().idToken
        if (idToken) {
            config.headers.Authorization = `Bearer ${idToken}`
        }

        if (config.data instanceof FormData) {
            config.headers['Content-Type'] = undefined // Let Axios set the correct Content-Type for FormData
            console.info('[API] Using FormData for request', { url: config.url, data: config.data })
        }

        return config
    },
    async (error) => Promise.reject(error)
)

/**
 * Initializes the API client by fetching the tenant identifier and configuring the base URL.
 *
 * This function performs a one-time setup that:
 * - Fetches the tenant ID from the backend using the standard API instance
 * - Replaces the `{tenant}` placeholder in the base URL with the actual tenant value
 * - Sets the `isApiInitialized` flag to true once complete
 *
 * The initialization is idempotent and uses a promise to ensure it only runs once,
 * even if called multiple times concurrently. If initialization fails, the promise
 * is reset to allow retry on subsequent calls.
 */
async function initializeApi() {
    if (initializationPromise) return initializationPromise

    initializationPromise = (async () => {
        try {
            const response = await api.request<{ tenant: string }>({
                method: 'GET',
                url: '/tenant',
                baseURL: import.meta.env.VITE_BACKEND_URL ?? '/medendo/api',
                timeout: DEFAULT_REQUEST_TIMEOUT_MS,
            })
            const tenant = response.data.tenant
            api.defaults.baseURL = (api.defaults.baseURL ?? '').replace('{tenant}', tenant)
            isApiInitialized = true
            console.info(`[API] Initialized with tenant: ${tenant}. New baseURL: ${api.defaults.baseURL}`)
        } catch (error) {
            if (isWarningOnly(error)) {
                console.warn('[API] Failed to initialize tenant', error)
            } else {
                console.error('[API] Failed to initialize tenant', error)
            }
            // If initialization fails, we might want to retry later or use default
            initializationPromise = null
            throw error
        }
    })()

    return initializationPromise
}

/**
 * Determines whether a failed request was refused because the signed-in account has been
 * deactivated. Offboarding a user is a deliberate administrative act, so this is the expected
 * answer for them and not a fault to report.
 *
 * A plain 403 is not enough to conclude it: an invalid tenant answers with the same status, and
 * that one is a real fault.
 * @param error The error object received from an Axios request.
 * @returns True if the account behind the request has been deactivated.
 */
export function isAccountDisabled(error: unknown): boolean {
    if (!axios.isAxiosError(error) || error.response?.status !== 403) {
        return false
    }
    const data: unknown = error.response.data
    const detail = typeof data === 'object' && data !== null && 'detail' in data ? data.detail : undefined
    return detail === ACCOUNT_DISABLED_DETAIL
}

/**
 * Determines whether a failed accept-agreement/privacy-policy/usage-instructions request failed
 * only because another tab (or an earlier request from this one) already accepted it. The
 * backend's shared `_accept` helper has no other 400 branch, so this is the one case worth telling
 * apart from a real failure — the caller should treat it as success, not as an error to retry.
 * @param error The error object received from an Axios request.
 * @returns True if the account already had this flag set.
 */
export function isAlreadyAccepted(error: unknown): boolean {
    if (!axios.isAxiosError(error) || error.response?.status !== 400) {
        return false
    }
    const data: unknown = error.response.data
    const detail = typeof data === 'object' && data !== null && 'detail' in data ? data.detail : undefined
    return detail === ALREADY_ACCEPTED_DETAIL
}

/**
 * Determines if a given error is expected in normal operation (e.g. flaky mobile
 * connections) and should be logged as a warning instead of an error, keeping
 * Sentry free of unactionable noise.
 * @param error The error object received from an Axios request.
 * @returns True if the error should only be logged as a warning.
 */
function isWarningOnly(error: unknown): boolean {
    if (!axios.isAxiosError(error)) {
        return false
    }
    return (
        (error.code !== undefined && warningOnlyErrors.has(error.code)) ||
        warningOnlyStatuses.has(error.response?.status ?? 0) ||
        isAccountDisabled(error)
    )
}

/**
 * Determines whether a request was refused because the caller's credential was not accepted.
 * @param error The error object received from an Axios request.
 * @returns True if the response was a 401.
 */
function isUnauthorized(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 401
}

/**
 * Determines if a given error should trigger a retry for an API request.
 * Retries are performed for specific HTTP status codes (408, 429, 5xx) and network error codes.
 * A 401 is not among them; see `request`, which renews the credential once instead.
 * @param error The error object received from an Axios request.
 * @returns True if the request should be retried, false otherwise.
 */
export function shouldRetry(error: unknown): boolean {
    if (!axios.isAxiosError(error)) {
        return false
    }
    return (error.code !== undefined && RETRY_CODES.has(error.code)) || RETRY_STATUSES.has(error.response?.status ?? 0)
}

/**
 * Determines whether a failed request is worth keeping its payload around for. Only a failure that a
 * later attempt could plausibly survive qualifies: a rejected payload or a server error outside the
 * retry policy will fail again, so callers that queue their payloads must not wait on it (MD-1398).
 * @param error The error object received from an Axios request.
 * @returns True if a later attempt could still succeed, false if the failure is permanent.
 */
export function isTransientFailure(error: unknown): boolean {
    // A 401 is transient even though it is not in RETRY_STATUSES. The two answer different
    // questions: `request` has already renewed the credential once and will not spend more
    // attempts on it, but a queued payload outlives that request, and by the time it is tried
    // again the credential may well be valid. Treating it as permanent here would discard a batch
    // of a consultation's audio over an expired token.
    return !navigator.onLine || shouldRetry(error) || isUnauthorized(error)
}

/**
 * Renews the caller's Firebase credential, forcing past the refresher's throttle because a 401 is
 * proof the current token is not acceptable.
 *
 * A failure to renew is an answer, not an error: it means no retry can succeed. It is contained
 * here so it cannot replace the request's own error, which used to happen when the interceptor
 * awaited the refresher unguarded — `shouldRetry` then saw a Firebase error rather than an Axios
 * one, and the caller was handed something that did not describe its request.
 * @returns True if a new token was obtained.
 */
async function renewCredential(): Promise<boolean> {
    try {
        return await tokenRefresher.refresh({ force: true })
    } catch (error: unknown) {
        console.warn('[API] Token renewal failed', error)
        return false
    }
}

/**
 * Logs a request's final failure at the right level and flags a deactivated account.
 *
 * Every failure of every endpoint passes here, including the tenant initialization, so this is the
 * one place that sees a deactivation whichever request first runs into it. The app then has a dead
 * session and shows the account-disabled screen.
 * @param requestOrigin What this request was made for (see captureRequestOrigin), so a stale
 *     disabled-account response can't be applied once a different session is current by the
 *     time it actually arrives — whether that's a different account, or the same account signed
 *     out and back in. Not the raw idToken: a 401 partway through this same request can renew
 *     the credential, and that must not look like a different session either.
 * @returns The error, to be thrown by the caller.
 */
function reportFailure(
    error: unknown,
    method: Method,
    url: string,
    attempt: number,
    requestOrigin: RequestOrigin
): unknown {
    if (isAccountDisabled(error)) {
        sessionActions.accountDisabled(requestOrigin)
    }

    const code = axios.isAxiosError(error) ? error.code : undefined
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    const message = `[API] ${method} ${url} - FAILED after ${attempt} attempts [${status}] ${code}`

    if (isWarningOnly(error)) {
        console.warn(message, error)
    } else {
        console.error(message, error)
    }

    return error
}

/**
 * Makes an authenticated API request with built-in retry logic.
 * It attempts the request multiple times if `shouldRetry` returns true for an error.
 * @param method The HTTP method (e.g., "GET", "POST").
 * @param url The URL for the API endpoint.
 * @param data The payload for POST, PUT, PATCH requests.
 * @param retries The maximum number of times to retry the request (default: 0).
 * @param delay The delay in milliseconds between retries (default: 1000).
 * @returns A Promise that resolves with the response data or rejects with an error.
 */
export async function request<T = unknown>(
    method: Method,
    url: string,
    data: unknown,
    retries: number,
    delay: number,
    config: AxiosRequestConfig = {}
) {
    let attempt = 0
    // At most one credential renewal per request, however many attempts it makes.
    let renewed = false
    // Captured once, before the retry loop — not the raw token, see captureRequestOrigin — so a
    // mid-retry credential renewal (a new token, same session) isn't mistaken for a session
    // change by the time this request's failure is handled.
    const requestOrigin = captureRequestOrigin()

    while (attempt <= retries) {
        try {
            // Initialization runs inside the retry loop so a transient network
            // failure while fetching the tenant is retried like any other request.
            if (!isApiInitialized) {
                await initializeApi()
            }
            breadcrumb('API', `${method} ${url}`, { attempt })
            // `timeout` is set from `config.timeout` after the spread, not merged in ahead of it,
            // so a `config` that carries an explicit `timeout: undefined` (e.g. from an optional
            // caller-supplied value) still falls back to the default instead of silently reaching
            // Axios as "no timeout".
            const response = await api.request<T>({
                method,
                url,
                data,
                ...config,
                timeout: config.timeout ?? DEFAULT_REQUEST_TIMEOUT_MS,
            })
            console.info(`[API] ${method} ${url}`, {
                requestData: data,
                responseData: response.data,
                attempt: attempt + 1,
            })
            return response.data
        } catch (error: unknown) {
            attempt++

            // A 401 is worth exactly one retry, and only behind a renewal that actually produced a
            // new token. Renewing is the only thing that can change the outcome, so a second one is
            // pointless, and waiting `delay` first buys nothing when the credential is already new.
            // This request used to spend its whole budget here — five attempts over 20 seconds,
            // every one carrying the token the server had just rejected, because the renewal was
            // fire-and-forget in the response interceptor and could not report that it had done
            // nothing (PROD-REACT-SX). It also covers the 401s no renewal can help, such as an EHR
            // integration whose own session has expired (PROD-FASTAPI-DE): those stop at one.
            if (isUnauthorized(error)) {
                if (!renewed) {
                    renewed = true
                    breadcrumb('api', 'Unauthorized request')
                    showMessage(i18n.t('errors.unauthorized_refresh'), 'warning')
                    // Renew even with no retry budget left, so the next request starts from a
                    // credential the server will accept rather than repeating this one's 401.
                    const renewedNow = await renewCredential()
                    console.warn(
                        `[API] Attempt ${attempt} for ${method} ${url} was unauthorized. ` +
                            (renewedNow ? 'Token renewed.' : 'The token could not be renewed.')
                    )
                    if (renewedNow && attempt <= retries) continue
                }
                throw reportFailure(error, method, url, attempt, requestOrigin)
            }

            if (attempt <= retries && shouldRetry(error)) {
                console.warn(
                    `[API] Attempt ${attempt} failed for ${method} ${url}. Retrying in ${delay}ms. Error:`,
                    error
                )
                await new Promise((resolve) => setTimeout(resolve, delay))
                continue
            }

            throw reportFailure(error, method, url, attempt, requestOrigin)
        }
    }

    throw Error(`Failed to ${method} ${url} after ${retries + 1} attempts.`)
}

export const get = async <T = unknown>(
    url: string,
    retries: number = DEFAULT_RETRIES,
    delay: number = DEFAULT_RETRIES_DELAY,
    config: AxiosRequestConfig = {}
): Promise<T> => {
    return await request<T>('GET', url, null, retries, delay, config)
}

/**
 * Makes a GET request to an endpoint that returns a collection, rejecting a body that is not an array.
 *
 * A 2xx status is no guarantee of the declared shape: an empty body arrives as an empty string, and a
 * request answered by something other than the backend (an intercepting proxy, or nginx serving
 * index.html for an unrouted path) arrives as HTML. Such a body used to reach callers unchecked and
 * crash them on the first array method, far from the request that produced it (MD-1442).
 * @param url The URL for the API endpoint.
 * @returns A Promise that resolves with the array, or rejects if the body is not one.
 */
export const getList = async <T = unknown>(
    url: string,
    retries: number = DEFAULT_RETRIES,
    delay: number = DEFAULT_RETRIES_DELAY,
    config: AxiosRequestConfig = {}
): Promise<T[]> => {
    const data = await request<T[]>('GET', url, null, retries, delay, config)
    if (!Array.isArray(data)) {
        throw new TypeError(`[API] GET ${url} - expected an array body, got ${typeof data}`)
    }
    return data
}

export const post = async <T = unknown>(
    url: string,
    payload: unknown = null,
    retries: number = DEFAULT_RETRIES,
    delay: number = DEFAULT_RETRIES_DELAY,
    config: AxiosRequestConfig = {}
): Promise<T> => {
    return await request<T>('POST', url, payload, retries, delay, config)
}

export const put = async <T = unknown>(
    url: string,
    payload: unknown,
    retries: number = DEFAULT_RETRIES,
    delay: number = DEFAULT_RETRIES_DELAY,
    config: AxiosRequestConfig = {}
): Promise<T> => {
    return await request<T>('PUT', url, payload, retries, delay, config)
}

export const patch = async <T = unknown>(
    url: string,
    payload: unknown,
    retries: number = DEFAULT_RETRIES,
    delay: number = DEFAULT_RETRIES_DELAY,
    config: AxiosRequestConfig = {}
): Promise<T> => {
    return await request<T>('PATCH', url, payload, retries, delay, config)
}

export const del = async <T = unknown>(
    url: string,
    payload: unknown = null,
    retries: number = DEFAULT_RETRIES,
    delay: number = DEFAULT_RETRIES_DELAY,
    config: AxiosRequestConfig = {}
): Promise<T> => {
    return await request<T>('DELETE', url, payload, retries, delay, config)
}

// Add response interceptor for error handling
//
// A 401 used to be handled here, by awaiting a token refresh and rejecting regardless of what it
// did. The interceptor cannot see the attempt number, cannot stop the retry loop, and cannot report
// back to it, so the decision now lives in `request` where all three are available.
api.interceptors.response.use(
    (response) => {
        return response
    },
    async (error) => Promise.reject(error)
)

/**
 * WebSocket URL for a backend path, resolved against the same (tenant) base URL as every request.
 * Browsers cannot send an Authorization header on a WebSocket, so the caller authenticates in its first message.
 */
export function backendSocketUrl(path: string): string {
    const url = new URL(`${api.defaults.baseURL ?? ''}${path}`, window.location.href)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    return url.toString()
}

/**
 * URL of a backend path, for a request that cannot go through axios: a streamed response body is read
 * with fetch. Resolves the tenant first, as `request` does, so the URL never carries the `{tenant}` placeholder.
 */
export async function apiUrl(path: string): Promise<string> {
    if (!isApiInitialized) await initializeApi()
    return `${api.defaults.baseURL ?? ''}${path}`
}
